import {
	Alert,
	Anchor,
	Group,
	Loader,
	SimpleGrid,
	Stack,
	Text,
	Title,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router";

import {
	ApproveDialog,
	CommentDialog,
} from "@/features/verification/decision-dialogs";
import { useRequestDetail } from "@/features/verification/use-request-detail";
import { useRequestMutations } from "@/features/verification/use-request-mutations";
import {
	VerificationDecision,
	VerificationDocuments,
	VerificationHistory,
	VerificationSubmitter,
} from "@/features/verification/verification-panels";
import { describeApiErrorSync } from "@/shared/api/error-message";
import { StatusBadge } from "@/shared/ui/status-badge";

export function VerificationPage() {
	const { t } = useTranslation();
	const { id } = useParams<{ id: string }>();
	const detailQuery = useRequestDetail(id);
	const mutations = useRequestMutations(id ?? "");
	const [approveOpened, approveHandlers] = useDisclosure(false);
	const [declineOpened, declineHandlers] = useDisclosure(false);
	const [reopenOpened, reopenHandlers] = useDisclosure(false);

	if (!id) {
		return <Alert color="red">{t("verification.missingId")}</Alert>;
	}

	if (detailQuery.isLoading) {
		return (
			<Group justify="center" py="xl">
				<Loader />
			</Group>
		);
	}

	if (detailQuery.error) {
		return (
			<Alert color="red" title={t("verification.loadError")}>
				{describeApiErrorSync(detailQuery.error)}
			</Alert>
		);
	}

	const detail = detailQuery.data;
	if (!detail) {
		return null;
	}

	return (
		<Stack gap="md">
			<Group justify="space-between" align="flex-start">
				<div>
					<Anchor component={Link} to="/applications" size="sm">
						{t("verification.back")}
					</Anchor>
					<Title order={2} mt={4}>
						{t("verification.title")}
					</Title>
					<Text c="dimmed" size="sm">
						{t("verification.round", { round: detail.round })} ·{" "}
						{detail.type === "CHILD"
							? t("applications.children")
							: t("applications.nationalDiscount")}
					</Text>
				</div>
				<StatusBadge status={detail.status} />
			</Group>

			<SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
				<VerificationDocuments
					detail={detail}
					onImageError={() => detailQuery.refetch()}
				/>
				<Stack gap="md">
					<VerificationSubmitter detail={detail} />
					<VerificationDecision
						awaitingDecision={detail.awaitingDecision}
						onApprove={approveHandlers.open}
						onDecline={declineHandlers.open}
						onRework={reopenHandlers.open}
					/>
					<VerificationHistory detail={detail} />
				</Stack>
			</SimpleGrid>

			<ApproveDialog
				opened={approveOpened}
				loading={mutations.approve.isPending}
				onClose={approveHandlers.close}
				onConfirm={(expiresAt) => {
					mutations.approve.mutate(expiresAt, {
						onSuccess: () => approveHandlers.close(),
					});
				}}
			/>
			<CommentDialog
				opened={declineOpened}
				title={t("verification.declineTitle")}
				confirmLabel={t("verification.decline")}
				confirmColor="red"
				loading={mutations.decline.isPending}
				onClose={declineHandlers.close}
				onConfirm={(comment) => {
					mutations.decline.mutate(comment, {
						onSuccess: () => declineHandlers.close(),
					});
				}}
			/>
			<CommentDialog
				opened={reopenOpened}
				title={t("verification.reworkTitle")}
				confirmLabel={t("verification.rework")}
				confirmColor="orange"
				loading={mutations.reopen.isPending}
				onClose={reopenHandlers.close}
				onConfirm={(comment) => {
					mutations.reopen.mutate(comment, {
						onSuccess: () => reopenHandlers.close(),
					});
				}}
			/>
		</Stack>
	);
}
